import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import styles from "./OptionDescription.module.scss";

interface Props {
  text?: string;
}

/**
 * CFTB-3457: the coach's own description of a booking option, on the cards under a
 * slot, clamped to two lines with "Read More" when there is more of it.
 *
 * The toggle is a span rather than a button on purpose: the card around it is itself
 * a button — picking the option — and a button inside a button is invalid, so the
 * browser drops one of them. It still behaves like a control (role, tab stop, Enter
 * and Space), and it stops the event so reading more does not also select the option.
 *
 * Its own component so each card measures its own text: whether two lines is a clamp
 * or the whole thing depends on the words and on how wide the card happens to be.
 */
const OptionDescription: React.FC<Props> = ({ text }) => {
  const { t } = useTranslation();
  const textRef = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [clamped, setClamped] = useState(false);

  useEffect(() => {
    const measure = () => {
      const element = textRef.current;
      if (!element || expanded) {
        return;
      }
      setClamped(element.scrollHeight > element.clientHeight + 1);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [text, expanded]);

  if (!text || text.trim() === "") {
    return null;
  }

  const toggle = (event: React.SyntheticEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setExpanded((open) => !open);
  };

  return (
    <div className={styles.wrapper}>
      <div
        ref={textRef}
        className={expanded ? styles.text : styles.text_clamped}
        data-testid="option-description"
      >
        {text}
      </div>
      {(clamped || expanded) && (
        <span
          role="button"
          tabIndex={0}
          className={styles.toggle}
          onClick={toggle}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              toggle(event);
            }
          }}
          data-testid="option-description-toggle"
        >
          {expanded ? t("Show Less") : t("Read More")}
        </span>
      )}
    </div>
  );
};

export default OptionDescription;
